// plugins/AuthService.js (optimizado)
import axios from 'axios'

const AuthService = {
  install(Vue, options) {
    let menusLoaded = false
    // Defaults
    if (!options.localStorageAuthInfo) options.localStorageAuthInfo = 'authInfo'

    // --- Auth info helpers ---
    function getAuthInfo() {
      return options.sessionStorage === true
        ? JSON.parse(sessionStorage.getItem(options.localStorageAuthInfo))
        : JSON.parse(localStorage.getItem(options.localStorageAuthInfo))
    }
    function setAuthInfo(authInfo) {
      if (options.sessionStorage === true)
        sessionStorage.setItem(options.localStorageAuthInfo, JSON.stringify(authInfo))
      else
        localStorage.setItem(options.localStorageAuthInfo, JSON.stringify(authInfo))
    }
    function deleteAuthInfo() {
      const key = options.localStorageAuthInfo || 'authInfo'
      if (options.sessionStorage === true) sessionStorage.removeItem(key)
      else localStorage.removeItem(key)
    }

    // --- Refresh helper (usable from guard) ---
    async function getRefreshToken() {
      if (!options.refreshApi) return false
      const info = getAuthInfo()
      if (!info?.refreshToken) return false
      try {
        const noAuth = axios.create({ baseURL: options.baseApi })
        const res = await noAuth.post(options.refreshApi, { token: info.refreshToken })
        if (!res?.data?.token) return false
        setAuthInfo(res.data)
        configureAxios()
        return true
      } catch {
        return false
      }
    }

    // --- Axios + Interceptor con cola de refresh ---
    let isRefreshing = false
    let refreshQueue = []
    function subscribeTokenRefresh(cb) { refreshQueue.push(cb) }
    function onRefreshed(token) { refreshQueue.forEach(cb => cb(token)); refreshQueue = [] }

    function configureAxios() {
      const http = axios.create({
        baseURL: options.baseApi,
        headers: { Authorization: getAuthInfo() ? `Bearer ${getAuthInfo().token}` : '' }
      })
      const noAuth = axios.create({ baseURL: options.baseApi })

      const interceptor = http.interceptors.response.use(
        r => r,
        async (error) => {
          const status = error?.response?.status
          const original = error.config || {}
          if (!(status === 401 || status === 403)) return Promise.reject(error)

          http.interceptors.response.eject(interceptor)
          const info = getAuthInfo()

          // Sin refresh posible -> login
          if (!options.refreshApi || !info?.refreshToken) {
            deleteAuthInfo()
            const loginPath = options.loginRoute || '/login'
            if (options.router.currentRoute.path !== loginPath) {
              await options.router.replace(loginPath)
            }
            return Promise.reject(error)
          }

          // Otro request ya refrescando -> encolar
          if (isRefreshing) {
            return new Promise(resolve => {
              subscribeTokenRefresh((newToken) => {
                original.headers = original.headers || {}
                original.headers.Authorization = 'Bearer ' + newToken
                original.__isRetryRequest = true
                resolve(http(original))
              })
            })
          }

          // Este request dispara el refresh
          isRefreshing = true
          try {
            const res = await noAuth.post(options.refreshApi, { token: info.refreshToken })
            if (!res?.data?.token) throw new Error('refresh sin token')

            setAuthInfo(res.data)
            configureAxios() // rehacer instancia con nuevo token
            isRefreshing = false
            onRefreshed(res.data.token)

            original.headers = original.headers || {}
            original.headers.Authorization = 'Bearer ' + res.data.token
            original.__isRetryRequest = true
            return http(original)
          } catch (e) {
            isRefreshing = false
            deleteAuthInfo()
            const loginPath = options.loginRoute || '/login'
            if (options.router.currentRoute.path !== loginPath) {
              await options.router.replace(loginPath)
            }
            return Promise.reject(e)
          }
        }
      )

      Vue.$http = http
    }

    // --- Router guard (auth/guest/públicas) ---
    function configureRouter() {
      const r = options.router
      r.beforeEach(async (to, from, next) => {
        const requiresAuth = to.matched.some(m => m.meta && m.meta.auth === true)
        const requiresMenu = to.matched.some(m => m.meta && m.meta.menu === true)
        const guestOnly = to.matched.some(m => m.meta && m.meta.auth === false)

        const info = getAuthInfo()
        const isAuthed = !!(info && info.token && info.id)

        if (requiresAuth) {
          if (isAuthed) {
            return next()
          }
          if (await getRefreshToken()) {
            return next()
          }
          deleteAuthInfo()
          return next(options.loginRoute || '/login')
        }

        if (guestOnly) {
          if (isAuthed) return next(options.defaultRoute || '/')
          return next()
        }

        return next()
      })
    }

    // --- Public API ---
    function configureAuthService() {
      const authService = {
        login: async function (credentials) {
          try {
            const res = await Vue.$http.post(options.loginApi, credentials)
            setAuthInfo(res.data)
            this.resetHttp()
            // 👇 carga menús apenas loguea (sin refresh)
            if (!menusLoaded && typeof options.menuLoader === 'function') {
              await options.menuLoader()
              menusLoaded = true
            }
            await options.router.replace(options.defaultRoute || '/')
            return res
          } catch (e) {
            throw e?.response || e
          }
        },
        loginDisponibilidad: async function (credentials) {
          try {
            const res = await Vue.$http.post(options.loginApi, credentials)
            setAuthInfo(res.data)
            this.resetHttp()
            await options.router.replace("/disponibilidad")
            return res
          } catch (e) {
            throw e?.response || e
          }
        },
        logout: async function () {
          try {
            await Vue.$http.post(options.logoutApi)
          } catch (_) { /* swallow */ }
          deleteAuthInfo()
          this.resetHttp()
          await options.router.replace(options.loginRoute || '/login')
        },
        isAuthenticated: function () {
          const info = getAuthInfo()
          return !!(info && info.token && info.id)
        },
        resetHttp: function () { configureAxios() },
        getAuthInfo: function () { return getAuthInfo() }
      }
      Vue.prototype.$auth = authService
    }

    // Boot plugin
    configureAxios()
    configureRouter()
    configureAuthService()
  }
}

export default AuthService
